import {
  BookMarked,
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
  Plus,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  getDifficultyColorClass,
  getDifficultyLabel,
} from '../../../lib/difficulty.js'
import type { CoachingHomeworkWithProblem } from '../problems/types'
import type {
  StudyPlan,
  StudyPlanProblem,
} from '../shared/hooks/use-study-plans'
import {
  groupStudyPlansBySource,
  useStudyPlans,
} from '../shared/hooks/use-study-plans'

interface StudyPlanWidgetProps {
  assignedHomework: CoachingHomeworkWithProblem[]
  onAssignProblems: (problemIds: string[]) => Promise<void>
  sourceId?: string
}

export function StudyPlanWidget({
  assignedHomework,
  onAssignProblems,
  sourceId,
}: StudyPlanWidgetProps) {
  const { studyPlans, loading, error } = useStudyPlans(sourceId)
  const [isExpanded, setIsExpanded] = useState(false)
  const [addingPlanId, setAddingPlanId] = useState<string | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<StudyPlan | null>(null)
  const [selectedProblems, setSelectedProblems] = useState<Set<string>>(
    () => new Set(),
  )
  const [addingProblemIds, setAddingProblemIds] = useState<Set<string>>(
    () => new Set(),
  )

  // Get set of already assigned problem IDs
  const assignedProblemIds = useMemo(() => {
    return new Set(
      assignedHomework
        .filter((h) => h.status === 'active' && h.problemId)
        .map((h) => h.problemId as string),
    )
  }, [assignedHomework])

  // Calculate how many new problems each study plan would add
  const studyPlanStats = useMemo(() => {
    const stats = new Map<
      string,
      { total: number; new: number; alreadyAssigned: number }
    >()
    for (const plan of studyPlans) {
      const problemIds = plan.problems.map((p) => p.id)
      const newProblems = problemIds.filter((id) => !assignedProblemIds.has(id))
      stats.set(plan.id, {
        total: problemIds.length,
        new: newProblems.length,
        alreadyAssigned: problemIds.length - newProblems.length,
      })
    }
    return stats
  }, [studyPlans, assignedProblemIds])

  const handleAddStudyPlan = async (plan: StudyPlan) => {
    const stats = studyPlanStats.get(plan.id)
    if (!stats || stats.new === 0) {
      toast.error('All problems from this study plan are already assigned.')
      return
    }

    setAddingPlanId(plan.id)
    try {
      // Get problem IDs that aren't already assigned
      const newProblemIds = plan.problems
        .map((p) => p.id)
        .filter((id) => !assignedProblemIds.has(id))

      await onAssignProblems(newProblemIds)
      toast.success(
        `Added ${newProblemIds.length} problem${newProblemIds.length > 1 ? 's' : ''} from "${plan.name}"`,
      )
    } catch (err) {
      console.error('Error adding study plan:', err)
      toast.error('Failed to add study plan problems')
    } finally {
      setAddingPlanId(null)
    }
  }

  const handleOpenPlanDetails = (plan: StudyPlan) => {
    setSelectedPlan(plan)
    setSelectedProblems(new Set())
  }

  const handleClosePlanDetails = () => {
    setSelectedPlan(null)
    setSelectedProblems(new Set())
    setAddingProblemIds(new Set())
  }

  const handleToggleProblem = (problemId: string) => {
    setSelectedProblems((prev) => {
      const next = new Set(prev)
      if (next.has(problemId)) {
        next.delete(problemId)
      } else {
        next.add(problemId)
      }
      return next
    })
  }

  const handleSelectAllUnassigned = () => {
    if (!selectedPlan) return
    const unassignedIds = selectedPlan.problems
      .filter((p) => !assignedProblemIds.has(p.id))
      .map((p) => p.id)
    setSelectedProblems(new Set(unassignedIds))
  }

  const handleDeselectAll = () => {
    setSelectedProblems(new Set())
  }

  const handleAddSingleProblem = async (problem: StudyPlanProblem) => {
    if (assignedProblemIds.has(problem.id)) return

    setAddingProblemIds((prev) => new Set(prev).add(problem.id))
    try {
      await onAssignProblems([problem.id])
      toast.success(`Added "${problem.title}"`)
    } catch (err) {
      console.error('Error adding problem:', err)
      toast.error('Failed to add problem')
    } finally {
      setAddingProblemIds((prev) => {
        const next = new Set(prev)
        next.delete(problem.id)
        return next
      })
    }
  }

  const handleAddSelectedProblems = async () => {
    if (selectedProblems.size === 0) return

    const problemIds = Array.from(selectedProblems)
    // Mark all as adding
    setAddingProblemIds(new Set(problemIds))

    try {
      await onAssignProblems(problemIds)
      toast.success(
        `Added ${problemIds.length} problem${problemIds.length > 1 ? 's' : ''}`,
      )
      setSelectedProblems(new Set())
    } catch (err) {
      console.error('Error adding problems:', err)
      toast.error('Failed to add problems')
    } finally {
      setAddingProblemIds(new Set())
    }
  }

  // Filter to only show study plans with problems, then group by source
  const availablePlans = useMemo(
    () => studyPlans.filter((plan) => plan.problems.length > 0),
    [studyPlans],
  )

  const groupedPlans = useMemo(
    () => groupStudyPlansBySource(availablePlans),
    [availablePlans],
  )

  if (loading) {
    return (
      <div className="bg-card border-border overflow-hidden rounded-lg border">
        <div className="flex items-center gap-2 p-4">
          <Loader2 size={18} className="text-muted-foreground animate-spin" />
          <span className="text-muted-foreground text-sm">
            Loading study plans...
          </span>
        </div>
      </div>
    )
  }

  if (error) {
    return null // Silently hide if there's an error
  }

  if (availablePlans.length === 0) {
    return null // Hide if no study plans available
  }

  return (
    <div className="bg-card border-border overflow-hidden rounded-lg border">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="border-border hover:bg-muted/50 flex w-full items-center justify-between border-b p-4 transition-colors"
      >
        <div className="flex items-center gap-2">
          <BookMarked size={18} className="text-muted-foreground" />
          <h2 className="text-foreground text-lg font-semibold">
            Quick Add from Study Plans
          </h2>
          <span className="bg-muted text-muted-foreground ml-2 rounded px-2 py-0.5 text-xs">
            {availablePlans.length} available
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp size={20} className="text-muted-foreground" />
        ) : (
          <ChevronDown size={20} className="text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4">
          <div className="space-y-4">
            {groupedPlans.map((group) => (
              <div key={group.sourceId}>
                {/* Source header */}
                <div className="mb-2 flex items-center gap-2">
                  <AuthoredIcon
                    icon={group.sourceIcon}
                    className="text-muted-foreground"
                    fallback={BookMarked}
                  />
                  <span className="text-muted-foreground text-sm font-medium">
                    {group.sourceName}
                  </span>
                </div>
                {/* Plans under this source */}
                <div className="space-y-2">
                  {group.plans.map((plan) => {
                    const stats = studyPlanStats.get(plan.id)
                    const isAdding = addingPlanId === plan.id
                    const allAssigned = stats?.new === 0
                    const planIcon = [
                      plan.icon,
                      group.sourceIcon,
                      'study-plan.webp',
                    ]

                    return (
                      <div
                        key={plan.id}
                        className="border-border bg-muted/30 ml-7 flex items-center justify-between rounded-lg border p-3"
                      >
                        <button
                          type="button"
                          onClick={() => handleOpenPlanDetails(plan)}
                          className="flex flex-1 cursor-pointer items-center gap-3 text-left hover:opacity-80"
                        >
                          <AuthoredIcon
                            icon={planIcon}
                            className="text-muted-foreground"
                            fallback={BookMarked}
                          />
                          <div>
                            <div className="text-foreground font-medium">
                              {plan.name}
                            </div>
                            <div className="text-muted-foreground text-xs">
                              {stats?.total} problem
                              {stats?.total !== 1 ? 's' : ''}
                              {stats && stats.alreadyAssigned > 0 && (
                                <span className="text-primary ml-1">
                                  ({stats.alreadyAssigned} already assigned)
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAddStudyPlan(plan)
                          }}
                          disabled={
                            isAdding || allAssigned || addingPlanId !== null
                          }
                          className={allAssigned ? 'opacity-50' : ''}
                        >
                          {isAdding ? (
                            <Loader2
                              size={14}
                              className="mr-1.5 animate-spin"
                            />
                          ) : allAssigned ? (
                            <Check size={14} className="mr-1.5" />
                          ) : (
                            <Plus size={14} className="mr-1.5" />
                          )}
                          {allAssigned
                            ? 'All Added'
                            : stats?.new === stats?.total
                              ? 'Add All'
                              : `Add ${stats?.new} New`}
                        </Button>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground mt-3 text-xs">
            Click a study plan to browse and select individual problems, or use
            the button to add all at once.
          </p>
        </div>
      )}

      {/* Study Plan Problems Dialog */}
      <Dialog open={!!selectedPlan} onOpenChange={handleClosePlanDetails}>
        <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AuthoredIcon
                icon={[
                  selectedPlan?.icon,
                  selectedPlan?.source?.icon,
                  'study-plan.webp',
                ]}
                className="text-muted-foreground"
                fallback={BookMarked}
              />
              {selectedPlan?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedPlan?.problems.length} problem
              {selectedPlan?.problems.length !== 1 ? 's' : ''} in this study
              plan
            </DialogDescription>
          </DialogHeader>

          {/* Selection controls */}
          <div className="flex items-center justify-between border-b pb-2">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSelectAllUnassigned}
                disabled={
                  !selectedPlan ||
                  selectedPlan.problems.every((p) =>
                    assignedProblemIds.has(p.id),
                  )
                }
              >
                Select all unassigned
              </Button>
              {selectedProblems.size > 0 && (
                <Button variant="ghost" size="sm" onClick={handleDeselectAll}>
                  Clear selection
                </Button>
              )}
            </div>
            {selectedProblems.size > 0 && (
              <span className="text-muted-foreground text-sm">
                {selectedProblems.size} selected
              </span>
            )}
          </div>

          {/* Problems list */}
          <div className="max-h-[50vh] overflow-y-auto">
            <div className="space-y-1">
              {selectedPlan?.problems.map((problem) => {
                const isAssigned = assignedProblemIds.has(problem.id)
                const isSelected = selectedProblems.has(problem.id)
                const isAddingThis = addingProblemIds.has(problem.id)

                return (
                  <div
                    key={problem.id}
                    className={`flex items-center gap-3 rounded-md p-2 transition-colors ${
                      isAssigned
                        ? 'bg-muted/30 opacity-60'
                        : isSelected
                          ? 'bg-primary/10'
                          : 'hover:bg-muted/50'
                    }`}
                  >
                    {/* Checkbox for multi-select */}
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleProblem(problem.id)}
                      disabled={isAssigned || isAddingThis}
                      className="border-border text-primary focus:ring-primary h-4 w-4 rounded"
                    />

                    {/* Problem info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-foreground truncate text-sm font-medium ${isAssigned ? 'line-through' : ''}`}
                        >
                          {problem.title}
                        </span>
                        {problem.difficulty && (
                          <span
                            className={`rounded px-1.5 py-0.5 text-xs font-medium ${getDifficultyColorClass(problem.difficulty)}`}
                          >
                            {getDifficultyLabel(problem.difficulty)}
                          </span>
                        )}
                      </div>
                      <div className="text-muted-foreground flex items-center gap-2 text-xs">
                        <span>{problem.source}</span>
                        {problem.externalId && (
                          <span className="text-muted-foreground/60">
                            #{problem.externalId}
                          </span>
                        )}
                        {isAssigned && (
                          <span className="text-primary">Already assigned</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      {problem.url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => window.open(problem.url!, '_blank')}
                        >
                          <ExternalLink size={14} />
                        </Button>
                      )}
                      {!isAssigned && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => handleAddSingleProblem(problem)}
                          disabled={isAddingThis}
                        >
                          {isAddingThis ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Plus size={14} />
                          )}
                        </Button>
                      )}
                      {isAssigned && (
                        <Check size={14} className="text-primary mr-2" />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClosePlanDetails}>
              Close
            </Button>
            <Button
              onClick={handleAddSelectedProblems}
              disabled={
                selectedProblems.size === 0 || addingProblemIds.size > 0
              }
            >
              {addingProblemIds.size > 0 ? (
                <>
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus size={14} className="mr-1.5" />
                  Add {selectedProblems.size} Selected
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
